import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type { CustomerCollectionsAgentConfig } from '@erp/rpc';

/**
 * Workflow "actions" available to bind (workflows with action_input/output
 * boundary steps). Drives the picker on the customer collections profile; each
 * option exposes its typed input sockets so the editor can attach agent
 * guidance per param.
 */
export function useWorkflowActionOptions() {
  const query = useQuery(
    orpc.workflows.listActions.queryOptions({ input: {} }),
  );
  return { ...query, options: query.data?.workflows ?? [] };
}

/**
 * Read + write a customer's collections agent-action config (the bound portal
 * actions + portal credential). Backed by per-customer entity_module_config.
 */
export function useCustomerAgentActionsConfig(customerId: string | null) {
  const queryClient = useQueryClient();

  const queryOptions =
    orpc.accountsReceivable.arCollectionsAgentActions.getCustomerConfig.queryOptions(
      { input: customerId ? { customerId } : skipToken },
    );
  const query = useQuery({ ...queryOptions, enabled: Boolean(customerId) });

  const updateMutation = useMutation(
    orpc.accountsReceivable.arCollectionsAgentActions.updateCustomerConfig.mutationOptions(
      {
        onSuccess: () =>
          void queryClient.invalidateQueries({
            queryKey: queryOptions.queryKey,
          }),
      },
    ),
  );

  return {
    ...query,
    config: query.data ?? null,
    update: (config: CustomerCollectionsAgentConfig) =>
      customerId
        ? updateMutation.mutateAsync({ customerId, config })
        : Promise.resolve(),
    isUpdating: updateMutation.isPending,
  };
}

/**
 * Thread-scoped agent actions: ask the agent to suggest a portal action with
 * generated params (detect + draft), and run a chosen action (approve).
 */
export function useThreadAgentActions(threadId: string) {
  const suggestMutation = useMutation(
    orpc.accountsReceivable.arCollectionsAgentActions.suggest.mutationOptions(
      {},
    ),
  );
  const runMutation = useMutation(
    orpc.accountsReceivable.arCollectionsAgentActions.run.mutationOptions({}),
  );

  return {
    suggest: () => suggestMutation.mutateAsync({ threadId }),
    suggestions: suggestMutation.data?.suggestions ?? [],
    isSuggesting: suggestMutation.isPending,
    hasSuggested: suggestMutation.isSuccess,
    run: (actionId: string, context: string) =>
      runMutation.mutateAsync({ threadId, actionId, context }),
    isRunning: runMutation.isPending,
  };
}

const TERMINAL_RUN_STATUSES = new Set([
  'completed',
  'failed',
  'cancelled',
  'terminated',
]);

/**
 * Poll a workflow run's status until it reaches a terminal state. Used by the
 * thread panel to show a "Pulling backup…" → "Done / Failed" chip after the
 * user approves an action.
 */
export function useWorkflowRunStatus(workflowRunId: string | null) {
  const query = useQuery({
    ...orpc.workflows.getRun.queryOptions({
      input: workflowRunId ? { workflowRunId } : skipToken,
    }),
    enabled: Boolean(workflowRunId),
    refetchInterval: (q) => {
      // Stop polling on a persistent fetch error — otherwise a failing backend
      // is hammered every 2s forever (TanStack v5 keeps polling through errors).
      if (q.state.status === 'error') return false;
      const status = q.state.data?.status;
      return status && TERMINAL_RUN_STATUSES.has(status) ? false : 2000;
    },
  });

  return {
    ...query,
    status: query.data?.status ?? null,
    stopReason: query.data?.stopReason ?? null,
    isTerminal: query.data
      ? TERMINAL_RUN_STATUSES.has(query.data.status)
      : false,
  };
}

/**
 * Poll a portal-action run for status + progress + the live operator session.
 * Richer than useWorkflowRunStatus (adds step progress and the web_browser
 * operator's sessionId) — used by the thread's run-status card to show progress
 * and embed the live browser view while the action runs.
 */
export function usePortalActionRunStatus(workflowRunId: string | null) {
  const query = useQuery({
    ...orpc.accountsReceivable.arCollectionsAgentActions.getRunStatus.queryOptions(
      { input: workflowRunId ? { workflowRunId } : skipToken },
    ),
    enabled: Boolean(workflowRunId),
    refetchInterval: (q) => {
      // Stop polling on a persistent fetch error — otherwise a failing backend
      // is hammered every 2s forever (TanStack v5 keeps polling through errors).
      if (q.state.status === 'error') return false;
      const status = q.state.data?.status;
      return status && TERMINAL_RUN_STATUSES.has(status) ? false : 2000;
    },
  });

  return {
    ...query,
    status: query.data?.status ?? null,
    stopReason: query.data?.stopReason ?? null,
    completedSteps: query.data?.completedSteps ?? 0,
    totalSteps: query.data?.totalSteps ?? 0,
    operator: query.data?.operator ?? null,
    isTerminal: query.data
      ? TERMINAL_RUN_STATUSES.has(query.data.status)
      : false,
  };
}
